import { store } from "store/actions/store";
import type { AppState } from "store/reducers/Reducer";

const BEARER_SESSION_STORAGE_KEY = "rivetai_ba_session_token";
const BEARER_SESSION_LOCAL_KEY = "rivetai_ba_session_token";

/** Legacy RivetAI access tokens are HS256 JWTs; Better Auth session tokens are opaque. */
export function isJwtToken(token: string): boolean {
  const parts = token.split(".");
  return parts.length === 3 && parts.every((part) => part.length > 0);
}

function readStorageItem(read: () => string | null): string | null {
  try {
    const value = read()?.trim();
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

export function setBearerSessionToken(token: string): void {
  if (!token) {
    return;
  }
  try {
    sessionStorage.setItem(BEARER_SESSION_STORAGE_KEY, token);
  } catch {
    // Storage may be unavailable in private mode.
  }
  try {
    localStorage.setItem(BEARER_SESSION_LOCAL_KEY, token);
  } catch {
    // Storage may be unavailable in private mode.
  }
}

export function getBearerSessionToken(): string | null {
  return (
    readStorageItem(() => sessionStorage.getItem(BEARER_SESSION_STORAGE_KEY)) ??
    readStorageItem(() => localStorage.getItem(BEARER_SESSION_LOCAL_KEY))
  );
}

export function clearBearerSessionToken(): void {
  try {
    sessionStorage.removeItem(BEARER_SESSION_STORAGE_KEY);
  } catch {
    // noop
  }
  try {
    localStorage.removeItem(BEARER_SESSION_LOCAL_KEY);
  } catch {
    // noop
  }
}

export function resolveAuthBearerToken(reduxToken: string | undefined | null): string {
  const token = reduxToken?.trim() ?? "";
  if (token.length > 0) {
    return token;
  }
  return getBearerSessionToken() ?? "";
}

/** Prefer opaque Better Auth session tokens over legacy JWTs stored in Redux. */
export function resolveBetterAuthBearerToken(): string {
  const cachedToken = getBearerSessionToken();
  if (cachedToken && !isJwtToken(cachedToken)) {
    return cachedToken;
  }

  const state = store.getState() as AppState;
  const reduxToken = resolveAuthBearerToken(state.auth.token);
  if (reduxToken && !isJwtToken(reduxToken)) {
    return reduxToken;
  }

  return cachedToken || reduxToken || "";
}
